import React from "react";
import { useQuery } from "@tanstack/react-query";
import { SGetKoboDepartmentSatisfaction } from "../../../apis/app.service";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import { colors } from "../../../theme/colors";
import {
  ChartLoading,
  ChartSyncNotice,
  GRID_STROKE,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  humanize,
  makeSegmentLabel,
  yAxisWidthFor,
} from "./chartHelpers";
import { useSyncPolling } from "./syncState";

// Categorical palette, assigned in fixed order and never cycled. The backend folds
// anything past the eighth department into a single "Other departments" bucket, which
// always wears the neutral swatch below.
const DEPARTMENT_COLORS = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#4a3aa7",
  "#e34948",
];
const OTHER_DEPARTMENT_LABEL = "Other departments";
const OTHER_DEPARTMENT_COLOR = "#7c7b76";

interface DepartmentRow {
  organization: string;
  totalResponses: number;
  overallScore: number;
  scores: Record<string, number>;
}

interface DepartmentSatisfactionProps {
  /** Kobo project id — the chart only renders for a single selected survey */
  survey?: string;
  organization: string | null;
  startDate: string;
  endDate: string;
  onSyncStatusChange?: (isSyncing: boolean) => void;
}

function DepartmentTooltip({ active, payload, label }: any) {
  if (!active || !payload || payload.length === 0) return null;

  const row: DepartmentRow | undefined = payload[0]?.payload?._row;
  const entries = payload
    .filter((entry: any) => entry.value !== undefined && entry.value !== null)
    .sort((a: any, b: any) => b.value - a.value);

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "0.75rem 0.9rem",
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        fontSize: "0.8rem",
      }}
    >
      <div style={{ fontWeight: 600, color: TEXT_PRIMARY }}>{label}</div>
      {row && (
        <div style={{ color: TEXT_SECONDARY, marginBottom: "0.5rem" }}>
          {row.overallScore}% overall • {row.totalResponses.toLocaleString()}{" "}
          responses
        </div>
      )}
      {entries.map((entry: any) => (
        <div
          key={entry.dataKey}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.1rem 0",
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              backgroundColor: entry.color,
              flexShrink: 0,
            }}
          />
          <span style={{ color: TEXT_SECONDARY, flex: 1 }}>{entry.name}</span>
          <span style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
            {entry.value}%
          </span>
        </div>
      ))}
    </div>
  );
}

function DepartmentSatisfaction({
  survey,
  organization,
  startDate,
  endDate,
  onSyncStatusChange,
}: DepartmentSatisfactionProps) {
  const { refetchInterval, resolve, reset } = useSyncPolling();

  const {
    data: response,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      "koboDepartmentSatisfaction",
      startDate,
      endDate,
      organization,
      survey,
    ],
    queryFn: () =>
      SGetKoboDepartmentSatisfaction({
        startDate,
        endDate,
        organization: organization || undefined,
        survey,
      }),
    enabled: Boolean(survey),
    refetchInterval,
    refetchOnWindowFocus: false,
  });

  const sync = resolve(response);
  const isSyncing = sync.state === "syncing";
  const syncEnded = sync.state === "failed" || sync.state === "stalled";

  const retrySync = React.useCallback(() => {
    reset();
    refetch();
  }, [reset, refetch]);

  React.useEffect(() => {
    if (onSyncStatusChange) {
      onSyncStatusChange(Boolean(isSyncing));
    }
  }, [onSyncStatusChange, isSyncing]);

  // Departments are stacked and colored in alphabetical order so a department keeps its
  // hue when the date range changes the volume ranking. The folded bucket stays last.
  const departments: string[] = React.useMemo(() => {
    const names: string[] = (response?.data?.departments || []).map(
      (department: any) => department.name,
    );
    const named = names
      .filter((name) => name !== OTHER_DEPARTMENT_LABEL)
      .sort((a, b) => humanize(a).localeCompare(humanize(b)));
    return names.includes(OTHER_DEPARTMENT_LABEL)
      ? [...named, OTHER_DEPARTMENT_LABEL]
      : named;
  }, [response]);

  const colorFor = React.useCallback(
    (department: string) => {
      if (department === OTHER_DEPARTMENT_LABEL) return OTHER_DEPARTMENT_COLOR;
      const index = departments.indexOf(department);
      return DEPARTMENT_COLORS[index % DEPARTMENT_COLORS.length];
    },
    [departments],
  );

  const rows = React.useMemo(() => {
    const organizations: DepartmentRow[] = response?.data?.organizations || [];
    return organizations.map((row) => ({
      organization: humanize(row.organization),
      _row: row,
      ...row.scores,
    }));
  }, [response]);

  const yAxisWidth = yAxisWidthFor(rows.map((row) => row.organization));

  const hasData = rows.length > 0 && departments.length > 0;
  const chartHeight = Math.max(320, rows.length * 68 + 90);

  return (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "20px",
        border: "1px solid #e0e0e0",
        overflow: "hidden",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      {/* header */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #e0e0e0",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: colors.bgPrimary,
        }}
      >
        <div style={{ padding: "1.25rem 2rem" }}>
          <h3 style={{ margin: 0, fontSize: "1.25rem", color: "#e0e0e0" }}>
            Average Satisfaction by Department
          </h3>
          <p
            style={{
              margin: "0.25rem 0 0 0",
              fontSize: "0.875rem",
              color: "#e0e0e0",
            }}
          >
            {hasData
              ? `${departments.length} departments across ${rows.length} ${
                  rows.length === 1 ? "organization" : "organizations"
                } • figures are % positive ratings`
              : "Percentage of positive ratings per department"}
          </p>
        </div>
      </div>

      {/* graph */}
      <div
        style={{
          paddingLeft: "1rem",
          paddingRight: "2rem",
          paddingTop: "2rem",
          paddingBottom: "1rem",
        }}
      >
        {isLoading || isSyncing ? (
          <ChartLoading
            isSyncing={Boolean(isSyncing)}
            label="Loading department satisfaction…"
          />
        ) : syncEnded ? (
          <ChartSyncNotice
            state={sync.state as "failed" | "stalled"}
            message={sync.message}
            onRetry={retrySync}
          />
        ) : hasData ? (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={rows}
              layout="vertical"
              stackOffset="expand"
              barCategoryGap="28%"
              margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
            >
              <CartesianGrid
                horizontal={false}
                strokeDasharray="3 3"
                stroke={GRID_STROKE}
              />
              <XAxis
                type="number"
                domain={[0, 1]}
                ticks={[0, 0.25, 0.5, 0.75, 1]}
                tickFormatter={(value: number) => `${Math.round(value * 100)}%`}
                tick={{ fontSize: 12, fill: TEXT_SECONDARY }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="organization"
                width={yAxisWidth}
                tick={{ fontSize: 12, fill: TEXT_SECONDARY }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={<DepartmentTooltip />}
                cursor={{ fill: "rgba(15,23,42,0.04)" }}
              />
              <Legend
                verticalAlign="bottom"
                align="center"
                iconType="square"
                iconSize={10}
                // Recharts defaults to sorting the legend alphabetically, which would
                // separate "Other departments" from the end of the list whenever some
                // department outranks it alphabetically. Keep the volume-ranked order.
                itemSorter={(item: any) =>
                  departments.map(humanize).indexOf(item.value)
                }
                wrapperStyle={{
                  paddingTop: "1rem",
                  fontSize: "0.8rem",
                  color: TEXT_SECONDARY,
                }}
              />
              {departments.map((department) => (
                <Bar
                  key={department}
                  dataKey={department}
                  name={humanize(department)}
                  stackId="department"
                  fill={colorFor(department)}
                  stroke="#ffffff"
                  strokeWidth={2}
                >
                  <LabelList
                    dataKey={department}
                    content={makeSegmentLabel(colorFor(department))}
                  />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "2rem",
              color: "#666",
            }}
          >
            No department data available for this survey and date range
          </div>
        )}
      </div>
    </div>
  );
}

export default DepartmentSatisfaction;
